import React from 'react';
import {
  SimpleForm,
  TextInput,
  FileInput,
  FileField,
  BooleanInput,
  DisabledInput,
  FormDataConsumer,
  Toolbar,
  SaveButton,
  DeleteButton,
  required,
} from 'react-admin';

// The API refuses to delete a layer mid-ingest (409): the running task would recreate
// the feature table right after the delete dropped it. Hide the button in that state
// rather than let the operator hit that 409 -- react-admin 2.x's Confirm dialog sets
// its own `loading` on confirm and never clears it, so ANY failed delete leaves the
// dialog stuck with both buttons disabled and no way out but a reload.
function LayerToolbar(props) {
  const { record } = props;
  // Same existence guard the default toolbar applies -- there is no record to delete
  // on the create screen.
  const canDelete =
    !!record && typeof record.id !== 'undefined' && record.status !== 'PROCESSING';
  return (
    <Toolbar {...props}>
      <SaveButton />
      {canDelete && <DeleteButton undoable={false} />}
    </Toolbar>
  );
}

// Publishing to GeoServer is only offered once a layer exists: it targets the
// layer's current shapefile, which only exists after the create-time upload.
function PublishingFields() {
  return (
    <FormDataConsumer>
      {({ formData }) => {
        // formData is briefly undefined while redux-form tears its state down on
        // save; without this guard that render throws and takes the whole screen
        // down to the error boundary.
        const publishing = (formData && formData.publishing) || {};
        // Reveal once a config exists (however it's currently enabled) or the
        // operator just checked the box above to start configuring one.
        if (!publishing.id && !publishing.enabled) {
          return null;
        }
        return (
          <>
            {/* SimpleForm wraps each of its direct children in a FormInput, but this
                whole group is one child, so its inputs would otherwise lay out inline
                and overlap. fullWidth gives each its own row. */}
            <TextInput
              source="publishing.publishedName"
              label="Nombre publicado (GeoServer)"
              validate={required('Debe ingresar el nombre a publicar')}
              fullWidth
            />
            {/* Sources are camelCase: the API transform converts to title_en/title_pt.
                Only ES is meaningful on its own -- a blank EN/PT is published as the
                Spanish title rather than left missing. */}
            <TextInput source="publishing.title" label="Título (ES)" fullWidth />
            <TextInput source="publishing.titleEn" label="Título (EN)" fullWidth />
            <TextInput source="publishing.titlePt" label="Título (PT)" fullWidth />
            <TextInput source="publishing.workspace" label="Workspace (opcional)" fullWidth />
            <TextInput source="publishing.store" label="Store (opcional)" fullWidth />
            <TextInput
              source="publishing.srs"
              label="SRS (opcional, ej. EPSG:4326)"
              fullWidth
            />
            <TextInput
              source="publishing.sld"
              label="Estilo (SLD)"
              multiline
              fullWidth
            />
            <FileInput
              source="publishing.sldFile"
              accept=".sld"
              label="...o subir un archivo .sld"
            >
              <FileField source="src" title="title" />
            </FileInput>
          </>
        );
      }}
    </FormDataConsumer>
  );
}

function LayerForm(props) {
  const isEdit = !!(props.record && props.record.id);

  return (
    <SimpleForm {...props} toolbar={<LayerToolbar />}>
      {isEdit ? (
        <DisabledInput source="name" label="Nombre de la capa" />
      ) : (
        <TextInput
          source="name"
          label="Nombre de la capa"
          validate={required('Debe ingresar un nombre')}
        />
      )}
      {!isEdit && (
        <FileInput
          source="files"
          multiple
          accept=".shp,.shx,.dbf,.prj,.cpg"
          label="Archivos del shapefile (.shp .shx .dbf .prj)"
          validate={required('Debe adjuntar los archivos del shapefile')}
        >
          <FileField source="src" title="title" />
        </FileInput>
      )}
      {/* The API refuses a replacement mid-ingest (409), so don't offer one then. */}
      {isEdit && props.record.status !== 'PROCESSING' && (
        <FileInput
          source="files"
          multiple
          accept=".shp,.shx,.dbf,.prj,.cpg"
          label="Reemplazar datos (shapefile: .shp .shx .dbf .prj)"
          placeholder={
            <p>
              Opcional. Arrastre o seleccione los archivos del nuevo shapefile. Al guardar se
              reprocesa la capa, se recalculan sus métricas y, si está publicada en GeoServer,
              se republica.
            </p>
          }
        >
          <FileField source="src" title="title" />
        </FileInput>
      )}
      {isEdit && (
        <>
          <BooleanInput
            source="publishing.enabled"
            label="Publicar en GeoServer"
            defaultValue={true}
          />
          <PublishingFields />
        </>
      )}
    </SimpleForm>
  );
}

export default LayerForm;
